import { useState } from 'react';

import { toastService } from '@thefarmersfront/kitchen';

import { useAPI, useAsyncHandler } from '~/hooks/common';

import { API_ENDPOINT, TOAST_MESSAGE } from '~/constants';

import type { TypeErrorResponseData } from '~/types/common.types';
import type {
  TypeGetVendorReleaseDetailItemsParams,
  TypeGetVendorReleaseDetailItemsResponse,
  TypeGetVendorReleaseDetailResponse,
} from '~/types/stock.types';

/**
 * [재고관리] 반출 요청 상세 api 요청 훅
 */
const useGetVendorReleaseDetail = () => {
  const { runAsync } = useAsyncHandler();

  const [responseData, setResponseData] = useState<TypeGetVendorReleaseDetailResponse['data'] | null>(null);
  const [responseItemsData, setResponseItemsData] = useState<TypeGetVendorReleaseDetailItemsResponse | null>(null);

  /**
   * 반출 요청 상세 정보 조회
   */
  const getVendorReleaseId = useAPI<unknown, TypeGetVendorReleaseDetailResponse>({
    endpoint: API_ENDPOINT.STOCK.GET_VENDOR_RELEASE_ID,
    method: 'get',
  });

  const requestVendorReleaseDetail = async (
    id: number,
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (data: TypeGetVendorReleaseDetailResponse['data']) => void;
      onError?: (error: TypeErrorResponseData['error']) => void;
    } = {}
  ) => {
    runAsync(() => getVendorReleaseId({ pathParams: `/${id}` }), {
      onSuccess: ({ data }) => {
        setResponseData(data);
        onSuccess?.(data);
      },
      onError: (error: TypeErrorResponseData['error']) => {
        toastService.addToast({
          type: 'error',
          message: error.message ?? TOAST_MESSAGE.DEFAULT_ERROR,
        });
        onError?.(error);
      },
    });
  };

  /**
   * 반출 요청 상세 상품 목록 조회
   */
  const getVendorReleaseIdItems = useAPI<
    TypeGetVendorReleaseDetailItemsParams,
    TypeGetVendorReleaseDetailItemsResponse
  >({
    endpoint: API_ENDPOINT.STOCK.GET_VENDOR_RELEASE_ID,
    method: 'get',
  });

  const requestVendorReleaseDetailItems = async (
    { id, ...params }: { id: number } & TypeGetVendorReleaseDetailItemsParams,
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (data: TypeGetVendorReleaseDetailItemsResponse) => void;
      onError?: (error: TypeErrorResponseData['error']) => void;
    } = {}
  ) => {
    runAsync(() => getVendorReleaseIdItems({ pathParams: `/${id}/items`, params }), {
      onSuccess: (res) => {
        setResponseItemsData(res);
        onSuccess?.(res);
      },
      onError: (error: TypeErrorResponseData['error']) => {
        toastService.addToast({
          type: 'error',
          message: error.message ?? TOAST_MESSAGE.DEFAULT_ERROR,
        });
        onError?.(error);
      },
    });
  };

  return { requestVendorReleaseDetail, responseData, requestVendorReleaseDetailItems, responseItemsData };
};

export default useGetVendorReleaseDetail;
